import { useCallback, useEffect, useState } from 'react'
import { Empty, ErrorBanner, SeverityPill, formatUtc, useAsync } from '../../components/Async'
import { PageTitle } from '../../components/PageTitle'
import { ApiError, explore, type EventQueryResult, type QueryProblem } from '../../lib/api'
import { nextOffset, pageState, previousOffset } from './paging'

const EXAMPLES = [
  { label: 'Critical this week', expression: 'severity >= 5 and occurred > -7d' },
  { label: 'Anything that failed', expression: 'succeeded = false' },
  { label: 'Credentials in an assistant', expression: "rules contains 'credentials'" },
  { label: 'One machine', expression: "deviceName startswith 'ws-'" }
]

/**
 * The query screen.
 *
 * Deliberately a small language rather than SQL. What an operator gets is a field from a fixed list,
 * an operator that field allows, and a value — which is enough for every question anybody asks of
 * this data and cannot express a question about somebody else's data.
 *
 * Validation runs as the query is typed and points at the character, because the alternative is a
 * red banner saying "syntax error" and a person counting quotation marks.
 */
export function ExplorePage() {
  const [expression, setExpression] = useState('severity >= 5 and occurred > -7d')
  const [result, setResult] = useState<EventQueryResult | null>(null)
  const [problems, setProblems] = useState<QueryProblem[]>([])
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [name, setName] = useState('')
  const [offset, setOffset] = useState(0)

  const fields = useAsync(() => explore.fields(), [])
  const saved = useAsync(() => explore.saved(), [])

  // Debounced: a keystroke should not be a request, and a validator that fires on every character
  // makes the editor feel slower than it is.
  useEffect(() => {
    const handle = setTimeout(async () => {
      try {
        setProblems((await explore.validate(expression)).problems)
      } catch {
        // A validation request that fails is not worth a banner; running the query will say so.
        setProblems([])
      }
    }, 250)

    return () => clearTimeout(handle)
  }, [expression])

  const runAt = useCallback(async (at: number) => {
    setBusy(true)
    setFailure(null)
    setNotice(null)
    try {
      setResult(await explore.run(expression, at))
      setOffset(at)
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The query could not be run.')
    } finally {
      setBusy(false)
    }
  }, [expression])

  // A new query always starts at the top; keeping the offset would silently show page three of a
  // result the operator has not seen page one of.
  const run = useCallback(() => runAt(0), [runAt])

  // A fetch rather than a link, because the token is in memory and a navigation carries no headers.
  // A failure now becomes a banner on this screen instead of replacing it with the browser's error
  // page and losing the expression the operator had composed.
  const exportCsv = useCallback(async () => {
    setBusy(true)
    setFailure(null)
    setNotice(null)
    try {
      await explore.exportCsv(expression)
      setNotice(`Exported the first ${explore.exportLimit} matching event(s).`)
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The export could not be produced.')
    } finally {
      setBusy(false)
    }
  }, [expression])

  async function save() {
    setBusy(true)
    setFailure(null)
    try {
      await explore.save(name, expression)
      setName('')
      saved.reload()
      setNotice('Saved.')
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The query could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="page-header">
        <div>
          <PageTitle>Explore</PageTitle>
          <p>
            Filter the event stream. Times accept a relative offset — <code>-7d</code>, <code>-2h</code> —
            so a saved query does not go stale the day after you save it.
          </p>
        </div>
      </div>

      <ErrorBanner message={failure} />
      {notice && (
        <div className="banner success" role="status" data-testid="query-notice">
          {notice}
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <div className="field">
            <label htmlFor="expression">Query</label>
            <textarea
              id="expression"
              style={{ minHeight: '4rem', fontFamily: 'var(--mono)' }}
              value={expression}
              onChange={(event) => setExpression(event.target.value)}
              data-testid="query-expression"
            />
          </div>

          {problems.length > 0 ? (
            <div data-testid="query-problems">
              {problems.map((problem, index) => (
                <div className="finding" key={index}>
                  <code>at {problem.position}</code>
                  <div>
                    <div>{problem.message}</div>
                    {problem.suggestion && <div className="muted">{problem.suggestion}</div>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="banner success" data-testid="query-valid">
              This query parses.
            </div>
          )}

          <div className="toolbar">
            <button
              type="button"
              className="primary"
              onClick={run}
              disabled={busy || problems.length > 0}
              data-testid="run-query"
            >
              Run
            </button>
            <button
              type="button"
              onClick={exportCsv}
              disabled={busy || problems.length > 0}
              data-testid="export-csv"
            >
              Export CSV
            </button>
            <input
              placeholder="Save as…"
              value={name}
              onChange={(event) => setName(event.target.value)}
              data-testid="query-name"
              style={{ marginLeft: 'auto', width: 'auto' }}
            />
            <button
              type="button"
              onClick={save}
              disabled={busy || problems.length > 0 || name.trim().length === 0}
              data-testid="save-query"
            >
              Save
            </button>
          </div>

          <p className="hint" data-testid="export-limit">
            An export carries the first {explore.exportLimit} matching events, ordered as the results
            below are. A result larger than that is cut, so narrow the query rather than exporting a
            slice of it.
          </p>

          <div className="toolbar" data-testid="query-examples">
            {EXAMPLES.map((example) => (
              <button key={example.label} type="button" onClick={() => setExpression(example.expression)}>
                {example.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {saved.value && saved.value.length > 0 && (
        <div className="card">
          <div className="card-body">
            <h3 style={{ marginTop: 0 }}>Saved</h3>
            <div className="toolbar" data-testid="saved-queries">
              {saved.value.map((query) => (
                <button
                  key={query.savedQueryId}
                  type="button"
                  onClick={() => setExpression(query.expression)}
                  title={`${query.expression} · run ${query.runCount} time(s)`}
                >
                  {query.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className="card">
          <div className="card-body toolbar">
            {/* The total was already honest here; what was missing was any way to reach past the
                hundredth row, and any statement that a hundred was all there was. */}
            <span data-testid="query-summary">
              {pageState(result.total, offset, result.items.length).summary} Ran in{' '}
              {result.elapsedMilliseconds} ms.
            </span>
            <div className="toolbar" style={{ marginLeft: 'auto' }} data-testid="query-paging">
              <button
                type="button"
                onClick={() => runAt(previousOffset(offset, 100))}
                disabled={busy || !pageState(result.total, offset, result.items.length).canGoBack}
                data-testid="query-previous"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => runAt(nextOffset(result.total, offset, 100))}
                disabled={busy || !pageState(result.total, offset, result.items.length).canGoForward}
                data-testid="query-next"
              >
                Next
              </button>
            </div>
          </div>

          <div className="table-scroll">
            <table data-testid="query-results">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Device</th>
                  <th>Severity</th>
                  <th>What happened</th>
                  <th className="secondary">Class</th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((row) => (
                  <tr key={row.eventRecordId}>
                    <td>{formatUtc(row.occurredUtc)}</td>
                    <td>{row.deviceName}</td>
                    <td>
                      <SeverityPill severityId={row.severityId} />
                    </td>
                    <td>
                      {row.message}
                      {row.matchedRuleNames && <div className="muted">{row.matchedRuleNames}</div>}
                    </td>
                    <td className="muted secondary">{row.className}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {result.items.length === 0 && <Empty>Nothing matched.</Empty>}
        </div>
      )}

      {fields.value && (
        <div className="card">
          <div className="card-body">
            <h3 style={{ marginTop: 0 }}>What you can filter on</h3>
            <dl className="detail" data-testid="query-fields">
              {fields.value.map((field) => (
                <div key={field.name} style={{ display: 'contents' }}>
                  <dt className="mono">{field.name}</dt>
                  <dd>{field.description}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}
    </>
  )
}
